import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, TimeoutError, of, throwError, timer } from 'rxjs';
import { catchError, map, retry, switchMap, timeout } from 'rxjs/operators';

// Fiabilité d'un géocodage. Le score BAN NE SUFFIT PAS : c'est une distance de chaînes (addok)
// entre la requête et le libellé, pas une précision. Il dépend même de la FORME de la requête —
// la même bonne réponse vaut 0,96 avec le CP dans q et 0,40 avec le CP en filtre postcode=.
// Le verdict combine donc quatre signaux : type retourné, couverture des jetons saisis,
// concordance du code postal, et écart au second candidat.
//   'exact'   : point au numéro, l'adresse saisie se retrouve dans le libellé -> persistable
//   'approx'  : axe de rue ou commune, ou couverture partielle -> affichable, PAS persistable seul
//   'douteux' : le libellé retenu ne correspond pas à l'adresse saisie -> à faire confirmer
export type GeoTrust = 'exact' | 'approx' | 'douteux';

// Résultat de géocodage : coordonnées + libellé normalisé + tout ce qui permet d'en juger.
export interface GeoAddress {
	lat: number;
	lon: number;
	label: string;
	score: number;
	type: string;					// housenumber | street | locality | municipality | poi
	postcode: string;				// CP RENVOYÉ par la BAN — à comparer au CP saisi
	citycode: string;				// code INSEE de la commune trouvée
	scoreNext: number;			// score du 2e candidat (0 s'il n'y en a pas) : mesure l'ambiguïté
	coverage: number;			// part des jetons de l'adresse saisie retrouvés dans le libellé [0..1]
	trust: GeoTrust;
}

// Requête de géocodage : la voie d'un côté, les FILTRES EXACTS de l'autre. Le code postal ne doit
// plus être concaténé dans q — passé en filtre postcode=, il ne peut plus être « à peu près »
// satisfait par une commune homonyme d'un autre département.
export interface GeoQuery {
	q: string;						// variante principale (les trois lignes recollées) — clé de lecture
	variants: string[];		// variantes ORDONNÉES, de la plus complète à la plus resserrée
	postcode: string;			// filtre exact BAN ; JAMAIS city= (voir GEO_CITY_WARNING)
	label: string;				// « voie CP ville » — affichage et clé de cache uniquement
	tokens: string[];			// jetons significatifs de la voie saisie, pour la couverture
	name: string;					// raison sociale : recours index=poi quand la voie ne donne rien
}

// Issue d'un géocodage pour l'écran : `injoignable` = la BAN n'a pas répondu (délai, réseau, quota, 5xx), réessayer a un sens.
export interface GeoOutcome {
	geo: GeoAddress | null;
	injoignable: boolean;
}

// Base Adresse Nationale servie par la Géoplateforme IGN (successeur d'api-adresse.data.gouv.fr).
// Gratuit, sans clé, adresses françaises uniquement. Quota mesuré dans les en-têtes de réponse :
// `ratelimit-limit: 50` (50 requêtes/seconde par IP). Pour du volume, préférer l'endpoint CSV
// de masse — voir tools/geo-bench.mjs, qui géocode des milliers d'adresses en une requête.
export const BAN_URL = 'https://data.geopf.fr/geocodage/search';

// PIÈGE VÉRIFIÉ — ne jamais passer city= : contrairement à postcode=, ce n'est pas un filtre exact
// mais une correspondance floue sur le nom de commune. « city=LA GARDE » ramène La Garde-Adhémar
// (26700, Drôme), à 175 km, et fait DISPARAÎTRE les résultats de La Garde (83130).
export const GEO_CITY_WARNING = 'city= est un filtre flou : utiliser postcode= ou citycode=';

// Calcul d'itinéraire de la Géoplateforme IGN (même famille : libre, sans clé, CORS ouvert)
export const GEOPF_ROUTE_URL = 'https://data.geopf.fr/navigation/itineraire';

// --- Cartes OSM maison (MiniMapComponent, DelrunsMapComponent) : configuration commune des tuiles ---
export const TILE_SIZE = 256;			// px par tuile (Web Mercator)
export const MAP_MIN_ZOOM = 3;
export const MAP_MAX_ZOOM = 18;
export const OSM_TILE_URL = 'https://tile.openstreetmap.org';	// usage courant OK (volume faible) — politique d'usage OSM à respecter

const GEOCODE_TIMEOUT_MS = 5000;
// Pause avant l'unique rejeu d'une requête BAN en panne passagère : le quota 429 se compte par seconde.
const GEOCODE_RETRY_MS = 1000;
const ROUTE_TIMEOUT_MS = 10000;

// La BAN répond 400 à un q qui ne commence ni par une lettre ni par un chiffre (« (Bat A) », « "Les Pins" », « ***… »).
const GEO_LEAD_PUNCT = /^[^\p{L}\p{N}]+/u;

// --- Normalisation de l'adresse avant envoi -------------------------------------------------
// Le fichier vient d'orthop, où la saisie était bornée à ~30 caractères par ligne : une voie longue
// déborde de street1 sur street2, et le complément d'accès se colle derrière. Les trois lignes sont
// donc RECOLLÉES puis nettoyées, au lieu de n'en lire qu'une.

// Bruit d'accès : coupé AVEC tout ce qui suit. « CODE 1818 A » à lui seul suffit à faire répondre
// zéro résultat au BAN. Deux familles, parce que certains de ces mots sont aussi des noms de voie :
//  - GEO_NOISE_SURE : jamais un nom de voie, on coupe dès qu'on les voit.
const GEO_NOISE_SURE = /(?:^|\s+)\b(BAT|BATIMENT|BATIMENTS|BT|ESC|ESCALIER|ETG|ETAGE|APPT|APT|APPARTEMENT|DIGICODE|INTERPHONE|RDC|CHEZ|CEDEX|BAL)\b.*$/i;
//  - GEO_NOISE_ID : ambigus (« Rue de la Porte Neuve » existe), on ne coupe que si un identifiant
//    suit — un jeton contenant un chiffre, ou une lettre isolée : « CODE 1818 A », « ENTREE 7 », « BP 12 ».
const GEO_NOISE_ID = /(?:^|\s+)\b(CODE|PORTE|ENTREE|BLOC|CAGE|BP|CS|BOITE)\b\s+(?=\w*\d|\w\b).*$/i;

// Abréviations postales AFNOR : la BAN indexe le libellé DÉVELOPPÉ. Mesuré sur le cas Eden Roc,
// « CH » au lieu de « CHEMIN » coûte 0,25 de score (0,709 -> 0,958) pour la même bonne réponse.
const GEO_ABREV: { [k: string]: string } = {
	AV: 'AVENUE', AVE: 'AVENUE', BD: 'BOULEVARD', BVD: 'BOULEVARD', BLD: 'BOULEVARD',
	CH: 'CHEMIN', CHE: 'CHEMIN', CHEM: 'CHEMIN', RTE: 'ROUTE', RT: 'ROUTE',
	IMP: 'IMPASSE', ALL: 'ALLEE', PL: 'PLACE', SQ: 'SQUARE', BRD: 'BOULEVARD',
	MTE: 'MONTEE', TRA: 'TRAVERSE', TRAV: 'TRAVERSE', RES: 'RESIDENCE', LOT: 'LOTISSEMENT',
	LD: 'LIEU-DIT', PROM: 'PROMENADE', SEN: 'SENTIER', QU: 'QUAI', CRS: 'COURS',
	ST: 'SAINT', STE: 'SAINTE', GAL: 'GENERAL', MAL: 'MARECHAL', PDT: 'PRESIDENT',
};

// Mots qui désignent une voie : servent à repérer, parmi les trois lignes, celle qui porte
// réellement la rue — l'information dont dépend le repli quand la ligne combinée ne donne rien.
const GEO_VOIE_RE = /\b(RUE|CHEMIN|AVENUE|BOULEVARD|ROUTE|IMPASSE|ALLEE|PLACE|TRAVERSE|MONTEE|QUAI|COURS|SENTIER|SQUARE|PROMENADE|VOIE|ROND[- ]POINT|CHE|CH|AV|BD|RTE|IMP|ALL|PL|MTE|TRA|CRS)\b/i;

// Raisons sociales qui désignent un ÉTABLISSEMENT, et pour lesquelles l'index POI a un sens.
// Sans ce filtre, le patronyme d'un particulier part dans un index de lieux et en ressort n'importe
// quoi : « DE CHAUME » a ramené une borne de recharge à 8 767 km, « MERLE » un technopôle.
const GEO_ETABLISSEMENT_RE = /\b(EHPAD|EHPA|MAISON DE RETRAITE|RETRAITE|CLINIQUE|HOPITAL|CENTRE|FOYER|RESIDENCE|MAS|FAM|IME|ITEP|SSIAD|USLD|ECOLE|COLLEGE|LYCEE|CAMPING|HOTEL|PHARMACIE|MAIRIE|CCAS|ASSOCIATION|INSTITUT|CABINET|LABORATOIRE|ETABLISSEMENT|SARL|SASU|SAS|SCI|EURL|SELARL|SNC)\b/i;

// Mots-outils : ignorés dans le calcul de couverture, ils ne discriminent rien.
const GEO_STOPWORDS = new Set(['DE', 'DU', 'DES', 'LA', 'LE', 'LES', 'L', 'D', 'A', 'AU', 'AUX', 'ET', 'EN', 'SUR', 'SOUS']);

// Seuils du verdict — établis sur le rejeu de 150 adresses réelles du fichier.
const GEO_COVERAGE_OK = 0.7;			// couverture au-dessus de laquelle le libellé rendu correspond bien à la saisie
const GEO_COVERAGE_MIN = 0.45;		// en dessous : le libellé parle d'autre chose que l'adresse saisie
const GEO_AMBIGUOUS_GAP = 0.05;		// écart au 2e candidat sous lequel le choix du 1er est arbitraire

// Résultat d'itinéraire : durée/distance totales + un tronçon par étape (portions de la Géoplateforme)
export interface GeoRoute {
	duration: number;		// secondes
	distance: number;		// mètres
	legs: { duration: number; distance: number }[];
}

@Injectable({
	providedIn: 'root'
})
export class GeoService {

	// HttpClient construit sur HttpBackend : contourne les intercepteurs de l'appli.
	// LoginService ajoute Authorization/X-Station-Id à toutes les requêtes → preflight CORS
	// rejeté par data.geopf.fr, et le JWT ne doit jamais partir vers un service tiers.
	private http = new HttpClient(inject(HttpBackend));

	// Cache mémoire de session, clé = libellé de la requête. Un écran de tournées émettait jusqu'à
	// 89 requêtes identiques ; le cache disque de DelrunsService ne couvre que la carte.
	private cache = new Map<string, GeoAddress | null>();

	// Un appel BAN. `autocomplete=0` parce qu'on géocode une adresse complète et non une frappe en
	// cours — le mode saisie gonfle les scores. `limit=5` pour disposer du second candidat, dont
	// l'écart au premier mesure l'ambiguïté. `index` vaut 'poi' pour le recours établissement.
	private search$(q: string, postcode: string, index?: string): Observable<any[]> {
		const params: { [k: string]: string } = { q, limit: '5', autocomplete: '0' };
		if (postcode) params['postcode'] = postcode;		// filtre EXACT — jamais city=, voir GEO_CITY_WARNING
		if (index) params['index'] = index;
		return this.http.get<any>(BAN_URL, { params }).pipe(
			timeout(GEOCODE_TIMEOUT_MS),
			// Un raté passager est rejoué une fois : la traîne de latence (p99 1,6 s, max 3,8 s au 29/09/2026) frôle le délai.
			retry({ count: 1, delay: (e) => (GeoService.isTransient(e) ? timer(GEOCODE_RETRY_MS) : throwError(() => e)) }),
			map((fc) => (fc?.features ?? []) as any[]),
		);
	}

	// Adresse -> meilleur résultat BAN jugé, ou null si vraiment introuvable.
	//
	// CASCADE sur les VARIANTES de la requête, dans l'ordre, en s'arrêtant à la première réponse
	// franche. Une seule variante suffit dans le cas nominal ; les suivantes rattrapent les adresses
	// dont une ligne (résidence, lotissement, quartier) noie le nom de la voie. Dernier recours :
	// l'index POI sur la raison sociale, qui retrouve les établissements — c'est lui qui place
	// « Maison de Retraite l'Eden Roc » à 26 m quand l'adresse postale ne suffit pas.
	//
	// Si aucune variante ne rend un résultat franc, on renvoie le MEILLEUR obtenu, marqué par son
	// verdict : mieux vaut afficher une position approchée signalée comme telle que rien du tout.
	// Ce que le code ne fait plus, c'est la présenter comme exacte — ni l'écrire en base tout seul.
	// Les erreurs réseau/timeout se PROPAGENT : l'appelant décide (cache, retry…). Sûr : geocode$.
	geocodeRaw$(query: GeoQuery): Observable<GeoAddress | null> {
		const postcode = (query?.postcode ?? '').trim();
		const name = (query?.name ?? '').trim().replace(GEO_LEAD_PUNCT, '');		// la raison sociale part en q du recours POI
		const variants = (query?.variants ?? []).filter((v) => v.trim().length >= 3);		// contrainte BAN : q >= 3
		if (!variants.length) return of(null);

		const essai$ = (i: number, meilleur: GeoAddress | null): Observable<GeoAddress | null> => {
			if (i >= variants.length) {
				// Toutes les variantes d'adresse sont épuisées : recours ÉTABLISSEMENT. Réservé aux
				// raisons sociales qui en désignent un — un patronyme de particulier n'a rien à faire
				// dans un index de lieux (voir GEO_ETABLISSEMENT_RE).
				if (name.length < 3 || !GEO_ETABLISSEMENT_RE.test(GeoService.tokenize(name).join(' '))) return of(meilleur);
				return this.search$(name, postcode, 'poi').pipe(
					map((fs) => {
						// Le POI se juge sur la RAISON SOCIALE, pas sur la voie : c'est elle qu'on a
						// envoyée. « Maison de Retraite l'Eden Roc » couvre 3 jetons sur 4 de
						// « MAISON DE RETRAITE EDEN ROCH » ; une borne de recharge n'en couvre aucun.
						const poi = GeoService.judge(fs, { ...query, tokens: GeoService.tokenize(name) });
						return poi && poi.trust !== 'douteux' ? poi : meilleur;
					}),
				);
			}
			return this.search$(variants[i], postcode).pipe(
				switchMap((fs) => {
					// Le verdict se juge sur LA variante envoyée, pas sur la requête d'origine :
					// c'est la couverture de ces jetons-là qui dit si le libellé rendu correspond.
					const r = GeoService.judge(fs, { ...query, tokens: GeoService.tokenize(variants[i]) });
					if (r && r.trust === 'exact') return of(r);
					return essai$(i + 1, GeoService.meilleur(meilleur, r));
				}),
			);
		};
		return essai$(0, null);
	}

	// Départage deux résultats : un verdict franc l'emporte, sinon le meilleur score.
	static meilleur(a: GeoAddress | null, b: GeoAddress | null): GeoAddress | null {
		if (!a) return b;
		if (!b) return a;
		const rang = (r: GeoAddress) => (r.trust === 'exact' ? 2 : r.trust === 'approx' ? 1 : 0);
		if (rang(a) !== rang(b)) return rang(a) > rang(b) ? a : b;
		return a.score >= b.score ? a : b;
	}

	// Variante UI : toute erreur (réseau, timeout) => null, et cache mémoire de session.
	// ATTENTION : un null ici ne distingue pas « adresse introuvable » de « BAN injoignable » (geocodeOutcome$ le fait).
	// Les appelants qui persistent doivent s'appuyer sur `trust`, pas sur la seule présence d'un point.
	geocode$(query: GeoQuery): Observable<GeoAddress | null> {
		return this.geocodeOutcome$(query).pipe(map((r) => r.geo));
	}

	// Même cache que geocode$, mais un échec d'appel est qualifié : `injoignable` (panne passagère) ou non (400, réponse illisible).
	geocodeOutcome$(query: GeoQuery): Observable<GeoOutcome> {
		const key = (query?.label ?? '').trim().toLowerCase();
		if (key && this.cache.has(key)) return of({ geo: this.cache.get(key) ?? null, injoignable: false });
		return this.geocodeRaw$(query).pipe(
			map((geo) => { if (key) this.cache.set(key, geo); return { geo, injoignable: false }; }),
			catchError((e) => of({ geo: null, injoignable: GeoService.isTransient(e) })),		// après le map : une erreur réseau n'est pas mise en cache
		);
	}

	// Panne passagère : délai dépassé, réseau (0), 408, quota (429), 5xx. Un 400 se reproduira : q qui commence par une ponctuation, par exemple.
	static isTransient(e: unknown): boolean {
		if (e instanceof TimeoutError) return true;
		return e instanceof HttpErrorResponse && (e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500);
	}

	// Retient le meilleur candidat et le JUGE. C'est ici que se joue la correction de fond : le code
	// prenait features[0] sans jamais regarder ni le type, ni le CP renvoyé, ni ce que le libellé
	// disait réellement — un « Allée de la Clairière » à 919 m était accepté comme une adresse exacte.
	static judge(features: any[], query: GeoQuery): GeoAddress | null {
		const f = features?.[0];
		if (!f?.geometry?.coordinates) return null;
		const p = f.properties ?? {};
		// Les features de l'index POI portent `toponym` et non `label` : sans ce repli, le recours
		// établissement remonterait un libellé vide et une couverture nulle.
		const label = p.label ?? p.toponym ?? '';
		const postcode = '' + (Array.isArray(p.postcode) ? p.postcode[0] : p.postcode ?? '');
		const citycode = '' + (Array.isArray(p.citycode) ? p.citycode[0] : p.citycode ?? '');
		const type = p._type === 'poi' ? 'poi' : (p.type ?? '');
		const score = +(p.score ?? 0);
		const scoreNext = +(features[1]?.properties?.score ?? 0);
		const coverage = GeoService.coverageOf(query?.tokens ?? [], label);

		// Un CP renvoyé différent du CP demandé signe un résultat hors commune : c'est ainsi qu'une
		// adresse de Toulon est ressortie à Bastia, 290 km plus loin.
		const cpOk = !query?.postcode || !postcode || postcode === query.postcode;
		const ambigu = scoreNext > 0 && score - scoreNext < GEO_AMBIGUOUS_GAP;

		let trust: GeoTrust;
		if (!cpOk || coverage < GEO_COVERAGE_MIN) trust = 'douteux';
		else if (type === 'municipality') trust = 'douteux';		// le point tombe sur la mairie, pas sur le client
		else if ((type === 'housenumber' || type === 'poi') && coverage >= GEO_COVERAGE_OK && !ambigu) trust = 'exact';
		else trust = 'approx';

		return {
			lon: +f.geometry.coordinates[0],		// GeoJSON : [lon, lat]
			lat: +f.geometry.coordinates[1],
			label, score, type, postcode, citycode, scoreNext, coverage, trust,
		};
	}

	// Part des jetons significatifs de l'adresse saisie qu'on retrouve dans le libellé rendu.
	// Contrôle GRATUIT (aucun appel) et décisif : sur le cas Eden Roc il vaut 0,25, « chapelle » —
	// le nom même de la voie — étant absent du libellé retenu. Le score BAN, lui, ne voyait rien.
	static coverageOf(tokens: string[], label: string): number {
		if (!tokens.length) return 1;		// rien à vérifier (adresse sans voie) : ne pas pénaliser
		const found = new Set(GeoService.tokenize(label));
		return tokens.filter((t) => found.has(t)).length / tokens.length;
	}

	// Jetons comparables : sans accents, sans ponctuation, sans mots-outils, abréviations développées.
	static tokenize(s: string): string[] {
		return (s ?? '')
			.normalize('NFD').replace(/[\u0300-\u036f]/g, '')		// « Chapelle Sainte-Marguerite » -> sans accents
			.toUpperCase().split(/[^A-Z0-9]+/)
			.map((t) => GEO_ABREV[t] ?? t)
			.filter((t) => t.length > 0 && !GEO_STOPWORDS.has(t));
	}

	// Itinéraire voiture : points = [départ, étapes…, arrivée]. UNE requête renvoie la durée
	// totale ET chaque tronçon (portions). < 2 points => null sans appel ; toute erreur => null.
	route$(points: { lat: number; lon: number }[]): Observable<GeoRoute | null> {
		if ((points?.length ?? 0) < 2) return of(null);
		const fmt = (p: { lat: number; lon: number }) => p.lon + ',' + p.lat;		// la Géoplateforme attend lon,lat
		const params: { [k: string]: string } = {
			resource: 'bdtopo-osrm',
			profile: 'car',
			optimization: 'fastest',
			start: fmt(points[0]),
			end: fmt(points[points.length - 1]),
			getSteps: 'false',
			timeUnit: 'second',
			distanceUnit: 'meter',
		};
		const mids = points.slice(1, -1).map(fmt).join('|');
		if (mids) params['intermediates'] = mids;
		return this.http.get<any>(GEOPF_ROUTE_URL, { params }).pipe(
			timeout(ROUTE_TIMEOUT_MS),
			map((r) => (r && r.duration !== undefined ? {
				duration: +r.duration,
				distance: +r.distance,
				legs: (r.portions ?? []).map((p: any) => ({ duration: +p.duration, distance: +p.distance })),
			} : null)),
			catchError(() => of(null)),
		);
	}

	// « 25 min », « 1 h 09 », « < 1 min » — '' si valeur absente
	static formatDuration(seconds: number | null | undefined): string {
		if (seconds === null || seconds === undefined || isNaN(+seconds)) return '';
		const min = Math.round(+seconds / 60);
		if (min < 1) return '< 1 min';
		if (min < 60) return min + ' min';
		return Math.floor(min / 60) + ' h ' + ('' + (min % 60)).padStart(2, '0');
	}

	// Requête de géocodage d'une adresse c_partner.
	//
	// Les TROIS lignes de rue sont recollées, au lieu de n'en retenir qu'une. L'ancienne version ne
	// lisait que street1 (ou street3) et écartait street2 comme « bâtiment : il pollue la recherche ».
	// C'est faux sur ce fichier : la saisie orthop était bornée à ~30 caractères, une voie longue
	// déborde donc sur la ligne suivante. Cas mesuré — 9 CH DE LA CHAPELLE **QUARTIER** / **STE
	// MARGUERITE** CODE 1818 A : le discriminant « Sainte Marguerite » était dans street2, et sans
	// lui la BAN renvoyait « Allée de la Clairière », à 919 m. 10 049 adresses du fichier portent
	// leur voie postale dans street2.
	//
	// Le bruit d'accès est retiré (« CODE 1818 A » seul suffit à faire répondre zéro résultat) et les
	// abréviations développées (« CH » -> « CHEMIN » : +0,25 de score). Le résidu de troncature en
	// fin de ligne (« QUARTIER ») est CONSERVÉ à dessein : le retirer ferait gagner du score, mais
	// « Quartier Saint-Jean » est aussi un nom de lieu légitime, et la requête aboutit sans cela.
	//
	// Le CP sort de `q` : passé en filtre exact postcode=, il ne peut plus être approximé par une
	// commune homonyme. La ville n'est PAS envoyée — city= est un filtre flou (GEO_CITY_WARNING).
	static geoQueryOf(a: {
		street1?: string | null; street2?: string | null; street3?: string | null;
		zip?: string | null; city?: string | null; name?: string | null;
	} | null): GeoQuery {
		const empty: GeoQuery = { q: '', variants: [], postcode: '', label: '', tokens: [], name: '' };
		if (!a) return empty;
		const zip = (a.zip ?? '').trim();
		const city = (a.city ?? '').trim();

		// Nettoyage LIGNE PAR LIGNE, jamais sur la chaîne recollée : le bruit coupe « tout ce qui
		// suit », et sur « les ibis entrée A » / « 57 Avenue Lebellegou » cela emporterait la vraie
		// rue, qui est sur la ligne d'après. (Régression vue au banc d'essai, scripts/geo-bench.mjs.)
		const lignes = [a.street1, a.street2, a.street3]
			.map((v) => GeoService.cleanLine(v)).filter((v) => !!v);
		if (!lignes.length) {
			const q = city.replace(GEO_LEAD_PUNCT, '');
			return { q, variants: q ? [q] : [], postcode: zip, label: [q, zip].filter((v) => !!v).join(' '), tokens: [], name: (a.name ?? '').trim() };
		}

		// Variantes ordonnées. La première suffit dans le cas nominal ; les suivantes rattrapent les
		// adresses où une ligne porte un nom de résidence ou de lotissement qui noie la voie —
		// « 17 AVENUE du carmel » + « villa josiane » ne répond RIEN une fois recollé, et répond
		// « 17 Avenue du Carmel » à 0,974 dès qu'on isole la ligne de voie.
		const rang = (s: string) => (/^\d/.test(s) ? 2 : 0) + (GEO_VOIE_RE.test(s) ? 1 : 0);
		const parRang = [...lignes].sort((x, y) => rang(y) - rang(x));
		const variants = [...new Set([lignes.join(' '), ...parRang])].filter((v) => v.length >= 3);

		const q = variants[0] ?? '';
		return {
			q, variants, postcode: zip,
			label: [q, zip, city].filter((v) => !!v).join(' '),
			tokens: GeoService.tokenize(q),
			name: (a.name ?? '').trim(),
		};
	}

	// Une ligne d'adresse : accents retirés, bruit d'accès coupé, abréviations développées.
	static cleanLine(v: string | null | undefined): string {
		const s = (v ?? '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			.replace(GEO_LEAD_PUNCT, '')		// avant le bruit : « (APPT 4) » se coupe comme « APPT 4 »
			.replace(GEO_NOISE_SURE, '').replace(GEO_NOISE_ID, '').trim();
		return s ? s.split(/\s+/).map((m) => GEO_ABREV[m.toUpperCase()] ?? m).join(' ') : '';
	}

	// Vrai s'il n'y a rien à envoyer au géocodeur (ni voie, ni commune).
	static isEmptyQuery(q: GeoQuery | null | undefined): boolean {
		return !q || !q.q;
	}

	// Requête vide : pour les points déjà positionnés (dépôts), qui n'ont rien à géocoder.
	static emptyQuery(): GeoQuery {
		return { q: '', variants: [], postcode: '', label: '', tokens: [], name: '' };
	}

	// Coordonnée brute (cuf Latitude/Longitude, éventuellement sérialisée en string) -> nombre ; null si absente,
	// vide ou non numérique.
	static numOf(v: any): number | null {
		return (v === null || v === undefined || v === '' || isNaN(+v)) ? null : +v;
	}

	// Position exploitable d'une adresse, null si une coordonnée manque OU si le couple vaut (0, 0).
	// (0, 0) n'est pas une position mais le « non renseigné » des lignes créées sans géocodage :
	// l'afficher pose le client au large de l'Afrique et empêche tout recalcul. Traité comme absent,
	// il repart au BAN. Aucune adresse française ne peut valoir (0, 0), l'amalgame est sans risque.
	static pointOf(lat: any, lon: any): { lat: number; lon: number } | null {
		const la = GeoService.numOf(lat);
		const lo = GeoService.numOf(lon);
		if (la === null || lo === null) return null;
		if (la === 0 && lo === 0) return null;
		return { lat: la, lon: lo };
	}

	// URL d'une tuile OSM (partagée par les deux cartes maison)
	static tileUrlOf(z: number, x: number, y: number): string {
		return `${OSM_TILE_URL}/${z}/${x}/${y}.png`;
	}

	// --- Projection Web Mercator : coordonnées -> px « monde » au zoom z (tuiles 256 px) ---
	// Source unique : les cartes (mini-map, delruns-map) utilisent ces formules, pas de copie locale.
	static lonToX(lon: number, z: number): number {
		return ((lon + 180) / 360) * 256 * Math.pow(2, z);
	}

	static latToY(lat: number, z: number): number {
		const r = (lat * Math.PI) / 180;
		return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 256 * Math.pow(2, z);
	}

	// --- Projection inverse : px « monde » au zoom z -> coordonnées (drag du marqueur, pan) ---
	static xToLon(x: number, z: number): number {
		return (x / (256 * Math.pow(2, z))) * 360 - 180;
	}

	static yToLat(y: number, z: number): number {
		const n = Math.PI * (1 - (2 * y) / (256 * Math.pow(2, z)));
		return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
	}
}
